import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, addDoc, getDocs, query, orderBy, where, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Report, Session, Expense } from '../../types/models';
import { CRALineCalculator } from '../../lib/calculateGST';
import { trackError, ERROR_CODES, createErrorMeta } from '../../lib/telemetry';

interface ReportState {
  reports: Report[];
  currentReport: Partial<Report> | null;
  isLoading: boolean;
  error: string | null;
  isGenerating: boolean;
}

const initialState: ReportState = {
  reports: [],
  currentReport: null,
  isLoading: false,
  error: null,
  isGenerating: false,
};

// Async thunks
export const fetchReports = createAsyncThunk(
  'reports/fetchReports',
  async (userId: string, { rejectWithValue }) => {
    try {
      const reportsRef = collection(db, 'users', userId, 'reports');
      const q = query(reportsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const reports: Report[] = [];
      querySnapshot.forEach(doc => {
        const data = doc.data();
        reports.push({
          id: doc.id,
          ...data,
          period: {
            start: data.period.start.toDate(),
            end: data.period.end.toDate(),
          },
          createdAt: data.createdAt,
        } as Report);
      });

      return reports;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const generateReport = createAsyncThunk(
  'reports/generateReport',
  async (
    {
      userId,
      period,
      provinceCode,
    }: {
      userId: string;
      period: { start: Date; end: Date };
      provinceCode: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // Fetch sessions for the period
      const sessionsRef = collection(db, 'users', userId, 'sessions');
      const sessionsQuery = query(
        sessionsRef,
        where('startTime', '>=', Timestamp.fromDate(period.start)),
        where('startTime', '<=', Timestamp.fromDate(period.end)),
        orderBy('startTime', 'asc')
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      const sessions: Session[] = [];
      sessionsSnapshot.forEach(doc => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          ...data,
          startTime: data.startTime,
          endTime: data.endTime,
        } as Session);
      });

      // Fetch expenses for the period
      const expensesRef = collection(db, 'users', userId, 'expenses');
      const expensesQuery = query(
        expensesRef,
        where('date', '>=', Timestamp.fromDate(period.start)),
        where('date', '<=', Timestamp.fromDate(period.end)),
        orderBy('date', 'asc')
      );
      const expensesSnapshot = await getDocs(expensesQuery);

      const expenses: Expense[] = [];
      expensesSnapshot.forEach(doc => {
        const data = doc.data();
        expenses.push({
          id: doc.id,
          ...data,
          date: data.date.toDate(),
          uploadedAt: data.uploadedAt,
        } as Expense);
      });

      // Calculate CRA lines
      const gstLines = CRALineCalculator.calculateGSTLines(sessions, expenses, provinceCode);
      const incomeSummary = CRALineCalculator.calculateIncomeTaxSummary(
        sessions,
        expenses,
        provinceCode
      );

      const reportData: Omit<Report, 'id'> = {
        userId,
        period,
        gstLines,
        totalIncome: incomeSummary.totalIncome,
        totalExpenses: incomeSummary.totalExpenses,
        totalITCs: gstLines.line106,
        totalGSTCollected: gstLines.line103,
        netGST: gstLines.line109,
        netIncome: incomeSummary.netIncome,
        createdAt: Timestamp.now(),
      };

      // Save report to Firestore
      const reportsRef = collection(db, 'users', userId, 'reports');
      const docRef = await addDoc(reportsRef, reportData);

      return {
        id: docRef.id,
        ...reportData,
      } as Report;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const previewReport = createAsyncThunk(
  'reports/previewReport',
  async (
    {
      userId,
      period,
      provinceCode,
    }: {
      userId: string;
      period: { start: Date; end: Date };
      provinceCode: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // Same logic as generateReport but don't save to Firestore
      const sessionsRef = collection(db, 'users', userId, 'sessions');
      const sessionsQuery = query(
        sessionsRef,
        where('startTime', '>=', Timestamp.fromDate(period.start)),
        where('startTime', '<=', Timestamp.fromDate(period.end)),
        orderBy('startTime', 'asc')
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      const sessions: Session[] = [];
      sessionsSnapshot.forEach(doc => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          ...data,
          startTime: data.startTime,
          endTime: data.endTime,
        } as Session);
      });

      const expensesRef = collection(db, 'users', userId, 'expenses');
      const expensesQuery = query(
        expensesRef,
        where('date', '>=', Timestamp.fromDate(period.start)),
        where('date', '<=', Timestamp.fromDate(period.end)),
        orderBy('date', 'asc')
      );
      const expensesSnapshot = await getDocs(expensesQuery);

      const expenses: Expense[] = [];
      expensesSnapshot.forEach(doc => {
        const data = doc.data();
        expenses.push({
          id: doc.id,
          ...data,
          date: data.date.toDate(),
          uploadedAt: data.uploadedAt,
        } as Expense);
      });

      const gstLines = CRALineCalculator.calculateGSTLines(sessions, expenses, provinceCode);
      const incomeSummary = CRALineCalculator.calculateIncomeTaxSummary(
        sessions,
        expenses,
        provinceCode
      );

      return {
        userId,
        period,
        gstLines,
        totalIncome: incomeSummary.totalIncome,
        totalExpenses: incomeSummary.totalExpenses,
        totalITCs: gstLines.line106,
        totalGSTCollected: gstLines.line103,
        netGST: gstLines.line109,
        netIncome: incomeSummary.netIncome,
        sessions,
        expenses,
      };
    } catch (error: any) {
      // Track report generation error
      const errorMeta = createErrorMeta(
        'reports',
        ERROR_CODES.REPORT_GEN_FAIL,
        'S2',
        'user-blocking',
        '24h',
        {
          retryable: true,
          providerCode: error.code,
          userAction: 'generate_report',
          extras: {
            userId,
            period: period?.start + ' to ' + period?.end,
          },
        }
      );
      trackError('Report generation failed', errorMeta, error);

      return rejectWithValue(error.message);
    }
  }
);

const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearCurrentReport: state => {
      state.currentReport = null;
    },
    setCurrentReport: (state, action) => {
      state.currentReport = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch reports
      .addCase(fetchReports.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.reports = action.payload;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Generate report
      .addCase(generateReport.pending, state => {
        state.isGenerating = true;
        state.error = null;
      })
      .addCase(generateReport.fulfilled, (state, action) => {
        state.isGenerating = false;
        state.reports.unshift(action.payload);
        state.currentReport = action.payload;
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.isGenerating = false;
        state.error = action.payload as string;
      })

      // Preview report
      .addCase(previewReport.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(previewReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentReport = action.payload;
      })
      .addCase(previewReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentReport, setCurrentReport } = reportSlice.actions;
export default reportSlice.reducer;
